import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Mail, Phone, MapPin, Clock, Send, MessageCircle } from 'lucide-react';

const ContactPage = () => {
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    inquiryType: 'general',
    product: ''
  });

  // Check for query parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const service = searchParams.get('service');
    const product = searchParams.get('product');
    
    if (service === 'buy-for-me') {
      setFormData(prev => ({
        ...prev,
        inquiryType: 'buyer',
        subject: product ? `Buy For Me Request: ${product}` : 'Buy For Me Service Request',
        product: product || '',
        message: product ? 
          `I would like to use the "Buy For Me" service for the following product: ${product}. Please provide details about the service fees and process.` : 
          'I would like to use the "Buy For Me" service. Please provide details about the service fees and process.'
      }));
    }
  }, [location.search]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
    alert('Thank you for your message! We\'ll get back to you soon.');
    setFormData({
      name: '',
      email: '',
      subject: '',
      message: '',
      inquiryType: 'general'
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-madeinbd-gradient text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Contact Us</h1>
          <p className="text-xl text-orange-100 max-w-3xl mx-auto">
            We're here to help you connect with the best of Bangladesh. Get in touch with us today!
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Send us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-madeinbd-500 focus:border-transparent"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-madeinbd-500 focus:border-transparent"
                      placeholder="your.email@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="inquiryType" className="block text-sm font-medium text-gray-700 mb-2">
                    Inquiry Type
                  </label>
                  <select
                    id="inquiryType"
                    name="inquiryType"
                    disabled={formData.inquiryType === 'buyer' && formData.product !== ''}
                    value={formData.inquiryType}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-madeinbd-500 focus:border-transparent"
                  >
                    <option value="general">General Inquiry</option>
                    <option value="seller">Become a Seller</option>
                    <option value="buyer">Product/Buying Inquiry</option>
                    <option value="buy-for-me">Buy For Me Service</option>
                    <option value="partnership">Partnership</option>
                    <option value="support">Technical Support</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    disabled={formData.inquiryType === 'buyer' && formData.product !== ''}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-madeinbd-500 focus:border-transparent"
                    placeholder="Brief subject of your inquiry"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    disabled={formData.inquiryType === 'buyer' && formData.product !== ''}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-madeinbd-500 focus:border-transparent"
                    placeholder="Please provide details about your inquiry..."
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-madeinbd-gradient text-white py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity flex items-center justify-center space-x-2"
                  title={formData.inquiryType === 'buyer' && formData.product !== '' ? 'Your Buy For Me request will be submitted' : 'Send your message'}
                >
                  <Send className="h-5 w-5" />
                  <span>{formData.inquiryType === 'buy-for-me' ? 'Submit Buy For Me Request' : 'Send Message'}</span>
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Get in Touch</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-8">
                  Have questions about our products, want to become a seller, or need support? 
                  We're here to help you every step of the way.
                </p>
              </div>

              {/* Contact Cards */}
              <div className="space-y-6">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div className="bg-madeinbd-100 p-3 rounded-full">
                      <Mail className="h-6 w-6 text-madeinbd-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Email Us</h3>
                      <p className="text-gray-600">hello@madeinbd.com</p>
                      <p className="text-sm text-gray-500">We'll respond within 24 hours</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div className="bg-blue-100 p-3 rounded-full">
                      <Phone className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Call Us</h3>
                      <p className="text-gray-600">+88 01770 156 736</p>
                      <p className="text-sm text-gray-500">Available 24 hours</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <div className="flex items-start space-x-4">
                    <div className="bg-purple-100 p-3 rounded-full">
                      <MapPin className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Visit Us</h3>
                      <p className="text-gray-600">57/8, Ground floor, East Rajabzar, West Panthapath, Dhaka 1215, Bangladesh.</p>
                      <p className="text-sm text-gray-500">Headquarters</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div className="bg-yellow-100 p-3 rounded-full">
                      <Clock className="h-6 w-6 text-yellow-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Business Hours</h3>
                      <p className="text-gray-600">24 Hrs</p>
                      <p className="text-sm text-gray-500">Available round the clock</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Links */}
              <div className="bg-madeinbd-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Help</h3>
                <div className="space-y-2">
                  {formData.inquiryType === 'buy-for-me' && (
                    <div className="bg-blue-50 p-4 rounded-lg mb-4">
                      <h4 className="font-medium text-blue-800 mb-2">About Buy For Me Service</h4>
                      <p className="text-blue-700 text-sm mb-2">
                        Our "Buy For Me" service allows you to purchase products through Made in BD even if you're unable to complete the transaction yourself.
                      </p>
                      <p className="text-blue-700 text-sm">
                        We charge a small service fee (typically 5-10% of the order value) and handle all aspects of the purchase, including payment processing, quality inspection, and shipping arrangements.
                      </p>
                    </div>
                  )}
                  <a href="#" className="flex items-center space-x-2 text-madeinbd-600 hover:text-madeinbd-700">
                    <MessageCircle className="h-4 w-4" />
                    <span>Seller Guidelines</span>
                  </a>
                  <a href="#" className="flex items-center space-x-2 text-madeinbd-600 hover:text-madeinbd-700">
                    <MessageCircle className="h-4 w-4" />
                    <span>Shipping Information</span>
                  </a>
                  <a href="#" className="flex items-center space-x-2 text-madeinbd-600 hover:text-madeinbd-700">
                    <MessageCircle className="h-4 w-4" />
                    <span>Return Policy</span>
                  </a>
                  <a href="#" className="flex items-center space-x-2 text-madeinbd-600 hover:text-madeinbd-700">
                    <MessageCircle className="h-4 w-4" />
                    <span>FAQs</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600">Find quick answers to common questions</p>
          </div>

          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">How can I become a seller on Made in BD?</h3>
              <p className="text-gray-700">Contact us through the form above or email us directly at hello@madeinbd.com. We'll guide you through our seller onboarding process.</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Do you ship internationally?</h3>
              <p className="text-gray-700">Yes, we ship to over 50 countries worldwide. Shipping costs and delivery times vary by location and product.</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">How do I verify product authenticity?</h3>
              <p className="text-gray-700">All our sellers are verified, and products are carefully curated. Look for the "Verified" badge and authentic product descriptions.</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">What payment methods do you accept?</h3>
              <p className="text-gray-700">We accept major credit cards, PayPal, and various local payment methods depending on your region.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;